<template>
    <div class="quote-view-page">
        <header class="page-header">
            <div class="header-content">
                <div class="header-title">
                    <h1>Visualizar Cotação</h1>
                    <p class="subtitle">Detalhes da sua cotação de seguro</p>
                </div>
                <div class="header-badge">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 12l2 2 4-4"></path>
                        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                        <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"></path>
                        <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3"></path>
                    </svg>
                    <span>Processo Seguro</span>
                </div>
            </div>
        </header>

        <main class="page-main">
            <div v-if="loading" class="loading-container">
                <div class="loading-spinner"></div>
                <p>Carregando cotação...</p>
            </div>

            <div v-else-if="error" class="error-container">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <h3>Erro ao carregar</h3>
                <p>{{ error }}</p>
                <button @click="retry" class="btn-retry">Tentar Novamente</button>
            </div>

            <div v-else class="quote-container">
                <!-- Status da Cotação -->
                <div class="status-section">
                    <div class="status-card">
                        <div class="status-info">
                            <h3>Status da Cotação</h3>
                            <div class="status-badge" :class="statusClass">
                                {{ statusText }}
                            </div>
                        </div>
                        <div class="quote-info">
                            <p><strong>Número da Cotação:</strong> {{ quoteData.ide }}</p>
                            <p><strong>Data de Criação:</strong> {{ formatDateTime(quoteData.createdDate) }}</p>
                        </div>
                    </div>
                </div>

                <!-- Dados da Cotação (Somente Leitura) -->
                <div class="quote-details">
                    <!-- Dados de Contato -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            Dados de Contato
                        </h3>

                        <div class="form-grid">
                            <div class="form-group">
                                <label>WhatsApp</label>
                                <div class="readonly-field">{{ maskContactData(quoteData.whatsApp || quoteData.whatsapp) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>E-mail</label>
                                <div class="readonly-field">{{ maskContactData(quoteData.email) || '-' }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Dados Pessoais -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            Dados Pessoais
                        </h3>

                        <div class="form-grid">
                            <div class="form-group">
                                <label>Nome Completo</label>
                                <div class="readonly-field">{{ quoteData.clientName || quoteData.name || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>CPF</label>
                                <div class="readonly-field">{{ formatCPF(quoteData.cpf) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Número da CNH</label>
                                <div class="readonly-field">{{ quoteData.cnhNumber || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Validade da CNH</label>
                                <div class="readonly-field">{{ formatDate(quoteData.cnhExpiration) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Data de Emissão da CNH</label>
                                <div class="readonly-field">{{ formatDate(quoteData.cnhIssueDate) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Sexo</label>
                                <div class="readonly-field">{{ getSexText(quoteData.sex) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Data de Nascimento</label>
                                <div class="readonly-field">{{ formatDate(quoteData.birthDate) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>CEP</label>
                                <div class="readonly-field">{{ formatCEP(quoteData.zipCodeHome) || '-' }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Dados do Veículo -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16.5 9 14 9h-1.1c-.3-.6-.9-1-1.6-1H9.7c-.7 0-1.3.4-1.6 1H7c-2.5 0-4.7 1.6-5.5 3.1C.7 12.3 0 13.1 0 14v2c0 .6.4 1 1 1h2"></path>
                                <circle cx="7" cy="17" r="2"></circle>
                                <circle cx="17" cy="17" r="2"></circle>
                            </svg>
                            Dados do Veículo
                        </h3>

                        <div class="form-grid">
                            <div class="form-group">
                                <label>Placa</label>
                                <div class="readonly-field">{{ formatPlate(quoteData.licensePlate) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>RENAVAM</label>
                                <div class="readonly-field">{{ quoteData.renavam || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Chassi</label>
                                <div class="readonly-field">{{ quoteData.chassis || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Marca</label>
                                <div class="readonly-field">{{ quoteData.brand || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Modelo</label>
                                <div class="readonly-field">{{ quoteData.model || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Ano</label>
                                <div class="readonly-field">{{ quoteData.year || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Cor</label>
                                <div class="readonly-field">{{ quoteData.color || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Tipo de Combustível</label>
                                <div class="readonly-field">{{ quoteData.fuelType || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Valor FIPE</label>
                                <div class="readonly-field">{{ formatCurrency(quoteData.fipeValue) || '-' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Zero KM</label>
                                <div class="readonly-field">{{ quoteData.isZeroKm ? 'Sim' : 'Não' }}</div>
                            </div>

                            <div class="form-group">
                                <label>Tipo de Uso</label>
                                <div class="readonly-field">{{ getUsageTypeText(quoteData.usageType) || '-' }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Arquivos Anexados -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7,10 12,15 17,10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Cotações
                        </h3>

                        <!-- Mostrar mensagem de aprovação quando status for "Aprovado" (4) -->
                        <div v-if="quoteData.situation === 4" class="quotation-approved">
                            <div class="approved-content">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <path d="M9 12l2 2 4-4"></path>
                                    <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                                    <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                                </svg>
                                <h3>Cotação Aprovada!</h3>
                                <p>Sua cotação foi aprovada com sucesso. Em breve entraremos em contato para finalizar o processo.</p>
                                <div class="approved-file-info">
                                    <p><strong>Corretora aprovada:</strong> {{ getApprovedFileName() }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Mostrar tabela se status for "Cotação concluída" (3), "Rejeitado" (5) ou "Arquivado" (6) -->
                        <div v-else-if="quoteData.situation === 3 || quoteData.situation === 5 || quoteData.situation === 6">
                            <div v-if="files.length > 0" class="files-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Corretora</th>
                                            <th>Data de Upload</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="file in files" :key="file.ide">
                                            <td class="broker-name">{{ file.description || 'Sem descrição' }}</td>
                                            <td>{{ formatDateTime(file.uploadDate) }}</td>
                                            <td>
                                                <div class="file-actions">
                                                    <a :href="file.downloadUrl" target="_blank" class="btn-download">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                            <polyline points="7,10 12,15 17,10"></polyline>
                                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                                        </svg>
                                                        Baixar
                                                    </a>
                                                    <!-- Botão de aprovar apenas para status "Cotação concluída" (3) -->
                                                    <button v-if="quoteData.situation === 3" @click="approveFile(file.ide)" class="btn-approve" :disabled="file.approved">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                            <path d="M9 12l2 2 4-4"></path>
                                                            <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                                                            <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                                                        </svg>
                                                        {{ file.approved ? 'Aprovado' : 'Aprovar' }}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div v-else class="no-files">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14,2 14,8 20,8"></polyline>
                                </svg>
                                <p>Nenhum documento anexado ainda.</p>
                            </div>
                        </div>

                        <!-- Mostrar mensagem informativa para outros status -->
                        <div v-else class="quotation-info">
                            <div class="info-content">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path d="M12 6v6l4 2"></path>
                                </svg>
                                <h3>Processando sua cotação</h3>
                                <p>Recebemos seu pedido de cotação e já estamos trabalhando para trazer as melhores opções para você.</p>
                                <p class="status-info">Status atual: <strong>{{ statusText }}</strong></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <!-- Help Section -->
        <div class="help-section">
            <div class="help-content">
                <div class="help-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                </div>
                <div class="help-text">
                    <h3>Precisa de Ajuda?</h3>
                    <p>Se você tiver alguma dúvida sobre sua cotação, entre em contato conosco:</p>
                    <div class="contact-info">
                        <a href="https://wa.me/5524993062444" target="_blank" class="contact-item">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            <span>WhatsApp: (24) 99306-2444</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <footer class="page-footer">
            <p>VR Tech</p>
        </footer>
    </div>
</template>

<script>
import apiConfig from '../config/api'
import api from '../services/api'

export default {
    name: 'QuoteViewPage',
    data() {
        return {
            quoteData: {},
            files: [],
            loading: true,
            error: null
        }
    },
    computed: {
        statusClass() {
            const status = this.quoteData.situation
            switch (status) {
                case 1: return 'status-received'
                case 2: return 'status-quoting'
                case 3: return 'status-sent'
                case 4: return 'status-approved'
                case 5: return 'status-rejected'
                case 6: return 'status-archived'
                default: return 'status-received'
            }
        },
        statusText() {
            const status = this.quoteData.situation
            switch (status) {
                case 1: return 'Recebido'
                case 2: return 'Cotando'
                case 3: return 'Aguardando sua aprovação'
                case 4: return 'Aprovado'
                case 5: return 'Rejeitado'
                case 6: return 'Arquivado'
                default: return 'Recebido'
            }
        }
    },
    async mounted() {
        await this.loadQuoteData()
    },
    methods: {
        async loadQuoteData() {
            try {
                this.loading = true
                const quoteId = this.$route.params.id

                if (!quoteId) {
                    this.error = 'ID da cotação não fornecido'
                    this.loading = false
                    return
                }

                // Verificar se há token de autenticação
                const token = localStorage.getItem('token')
                
                if (token) {
                    // Usar API autenticada para usuários logados
                    const quoteResponse = await api.get(`/vehicle-insurance/quotes/${quoteId}`)
                    this.quoteData = quoteResponse.data

                    const filesResponse = await api.get(`/vehicle-insurance/quotes/${quoteId}/files`)
                    this.files = (filesResponse.data || []).map(file => ({
                        ...file,
                        downloadUrl: `${apiConfig.baseURL}/vehicle-insurance/quotes/${quoteId}/files/${file.ide}/download`
                    }))
                } else {
                    // Usar API pública para usuários não autenticados
                    const quoteResponse = await this.fetchWithoutAuth(`/vehicle-insurance/quotes/${quoteId}/public`)
                    this.quoteData = quoteResponse

                    const filesResponse = await this.fetchWithoutAuth(`/vehicle-insurance/quotes/${quoteId}/files/public`)
                    this.files = (filesResponse || []).map(file => ({
                        ...file,
                        downloadUrl: `${apiConfig.baseURL}/vehicle-insurance/quotes/${quoteId}/files/${file.ide}/download`
                    }))
                }

                this.loading = false
            } catch (err) {
                console.error('Erro ao carregar cotação:', err)
                this.error = 'Erro ao carregar os dados da cotação. Tente novamente.'
                this.loading = false
            }
        },

        async fetchWithoutAuth(url) {
            // Fazer requisição sem token de autenticação
            const response = await fetch(`${apiConfig.baseURL}${url}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },

        async approveFile(fileId) {
            try {
                await api.post(`/vehicle-insurance/quotes/files/${fileId}/approve`)

                // Atualizar status da cotação para Aprovado (4)
                this.quoteData.situation = 4
                
                alert('Arquivo aprovado com sucesso! A cotação foi aprovada.')
                
                // Recarregar os dados para atualizar a interface
                await this.loadQuoteData()
            } catch (error) {
                console.error('Erro ao aprovar arquivo:', error)
                alert('Erro ao aprovar arquivo. Tente novamente.')
            }
        },

        retry() {
            this.loadQuoteData()
        },

        formatCPF(cpf) {
            if (!cpf) return ''
            return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        },

        formatCEP(cep) {
            if (!cep) return ''
            return cep.replace(/(\d{5})(\d{3})/, '$1-$2')
        },

        formatPlate(plate) {
            if (!plate) return ''
            return plate.replace(/([A-Z]{3})(\d{4})/, '$1-$2')
        },

        formatDate(date) {
            if (!date) return ''
            return new Date(date).toLocaleDateString('pt-BR')
        },

        formatDateTime(dateTime) {
            if (!dateTime) return ''
            return new Date(dateTime).toLocaleString('pt-BR')
        },

        formatCurrency(value) {
            if (!value) return ''
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(value)
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
        },

        getSexText(sex) {
            switch (sex) {
                case 1: return 'Masculino'
                case 2: return 'Feminino'
                default: return '-'
            }
        },

        getUsageTypeText(usageType) {
            switch (usageType) {
                case 1: return 'Particular'
                case 2: return 'Comercial'
                case 3: return 'Aluguel'
                default: return '-'
            }
        },

        maskContactData(data) {
            if (!data) return ''
            
            // Se o usuário estiver autenticado, mostrar dados completos
            const token = localStorage.getItem('token')
            if (token) {
                return data
            }
            
            // Para usuários não autenticados, mascarar os dados
            // Para WhatsApp (números)
            if (/^\d+$/.test(data)) {
                if (data.length >= 10) {
                    return data.substring(0, 2) + '****' + data.substring(data.length - 2)
                }
                return '****'
            }
            
            // Para email
            if (data.includes('@')) {
                const [localPart, domain] = data.split('@')
                if (localPart.length > 2) {
                    return localPart.substring(0, 2) + '****@' + domain
                }
                return '****@' + domain
            }
            
            // Para outros dados
            if (data.length > 4) {
                return data.substring(0, 2) + '****' + data.substring(data.length - 2)
            }
            
            return '****'
        },

        getApprovedFileName() {
            // Buscar o arquivo aprovado na lista de arquivos
            const approvedFile = this.files.find(file => file.approved === true)
            if (approvedFile) {
                return approvedFile.description || approvedFile.fileName || 'Sem descrição'
            }
            return 'Sem descrição'
        }
    }
}
</script>

<style scoped>
.quote-view-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.page-header {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding: 2rem 0;
}

.header-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-title h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
}

.subtitle {
    font-size: 1.125rem;
    color: #6b7280;
    margin: 0;
}

.header-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #10b981;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    font-weight: 600;
    font-size: 0.875rem;
}

.page-main {
    padding: 2rem 0;
}

.loading-container,
.error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    text-align: center;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.error-container svg {
    color: #ef4444;
    margin-bottom: 1rem;
}

.error-container h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
}

.error-container p {
    color: #6b7280;
    margin: 0 0 1.5rem 0;
}

.btn-retry {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
}

.btn-retry:hover {
    background: #2563eb;
}

.quote-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
}

.status-section {
    margin-bottom: 2rem;
}

.status-card {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.status-info h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 1rem 0;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 50px;
    font-weight: 600;
    font-size: 0.875rem;
}

.status-received {
    background: #d4edda;
    color: #155724;
}

.status-quoting {
    background: #cce5ff;
    color: #004085;
}

.status-sent {
    background: #fff3cd;
    color: #856404;
}

.status-approved {
    background: #d1ecf1;
    color: #0c5460;
}

.status-rejected {
    background: #f8d7da;
    color: #721c24;
}

.status-archived {
    background: #e2e3e5;
    color: #383d41;
}

.quote-info p {
    margin: 0.25rem 0;
    color: #6b7280;
    font-size: 0.875rem;
}

.quote-details {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.form-section {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 1.5rem 0;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e5e7eb;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-group label {
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
}

.readonly-field {
    padding: 0.75rem 1rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #374151;
    font-size: 0.875rem;
    min-height: 2.75rem;
    display: flex;
    align-items: center;
}

.files-table {
    overflow-x: auto;
}

.files-table table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.files-table th,
.files-table td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
}

.files-table th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
}

.files-table td {
    color: #6b7280;
    font-size: 0.875rem;
}

.broker-name {
    font-weight: bold;
    font-size: 1.275rem;
    color: #1f2937;
}

.file-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.btn-download,
.btn-approve {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
}

.btn-download {
    background: #3b82f6;
    color: white;
}

.btn-download:hover {
    background: #2563eb;
}

.btn-approve {
    background: #10b981;
    color: white;
}

.btn-approve:hover:not(:disabled) {
    background: #059669;
}

.btn-approve:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}

.no-files {
    text-align: center;
    padding: 3rem;
    color: #6b7280;
}

.no-files svg {
    margin-bottom: 1rem;
    opacity: 0.5;
}

.quotation-info {
    text-align: center;
    padding: 3rem 2rem;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    border-radius: 12px;
    border: 2px dashed #cbd5e1;
}

.info-content {
    max-width: 500px;
    margin: 0 auto;
}

.info-content svg {
    color: #3b82f6;
    margin-bottom: 1.5rem;
    opacity: 0.8;
}

.info-content h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 1rem 0;
}

.info-content p {
    color: #6b7280;
    font-size: 1rem;
    line-height: 1.6;
    margin: 0 0 1rem 0;
}

.status-info {
    background: #dbeafe;
    color: #1e40af;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    margin-top: 1.5rem !important;
}

.status-info strong {
    color: #1e40af;
    font-weight: 600;
}

.quotation-approved {
    text-align: center;
    padding: 3rem 2rem;
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    border-radius: 12px;
    border: 2px solid #10b981;
}

.approved-content {
    max-width: 500px;
    margin: 0 auto;
}

.approved-content svg {
    color: #10b981;
    margin-bottom: 1.5rem;
    opacity: 0.9;
}

.approved-content h3 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #064e3b;
    margin: 0 0 1rem 0;
}

.approved-content p {
    color: #065f46;
    font-size: 1rem;
    line-height: 1.6;
    margin: 0 0 1.5rem 0;
}

.approved-file-info {
    background: #ecfdf5;
    color: #047857;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    border: 1px solid #a7f3d0;
    font-size: 0.875rem;
    font-weight: 500;
}

.approved-file-info strong {
    color: #047857;
    font-weight: 600;
}

.page-footer {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    padding: 2rem 0;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
}

@media (max-width: 768px) {
    .header-content {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
    }

    .status-card {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
    }

    .form-grid {
        grid-template-columns: 1fr;
    }

    .file-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .files-table {
        font-size: 0.75rem;
    }

    .files-table th,
    .files-table td {
        padding: 0.5rem;
    }
}

/* Help Section Styles */
.help-section {
    margin: 2rem 1rem;
    padding: 0;
}

.help-content {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.help-icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    background: #3b82f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.help-text {
    flex: 1;
}

.help-text h3 {
    margin: 0 0 0.5rem 0;
    color: #1f2937;
    font-size: 1.125rem;
    font-weight: 600;
}

.help-text p {
    margin: 0 0 1rem 0;
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.5;
}

.contact-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.contact-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
    color: inherit;
}

.contact-item:hover {
    border-color: #3b82f6;
    box-shadow: 0 1px 3px rgba(59, 130, 246, 0.1);
    text-decoration: none;
    color: inherit;
}

.contact-item svg {
    color: #10b981;
    flex-shrink: 0;
}

.contact-item span {
    color: #374151;
    font-weight: 500;
    font-size: 0.875rem;
}

@media (max-width: 768px) {
    .help-content {
        flex-direction: column;
        text-align: center;
    }
    
    .help-icon {
        align-self: center;
    }
}
</style>
